import { formatMoney, formatSignedMoney } from "@/lib/money";

/** One payment-history row as the table stores it. */
export interface PaymentTransaction {
  id: string;
  created_at: string;
  transaction_type: string;
  description: string | null;
  amount_credits: number;
  balance_before_credits: number;
  balance_after_credits: number;
  idempotency_key?: string | null;
  metadata?: Record<string, unknown> | null;
}

/** How a row reads: its title, the amount column and the line under it. */
export interface PaymentRow {
  title: string;
  /** Why a failed top-up failed, in Stripe's words. */
  reason: string | null;
  amount: string;
  /** "Balance: $x", or what the amount means when it did not move the wallet. */
  note: string;
  tone: "credit" | "debit" | "card" | "failed";
}

/** The rest of the payment types, when the server left no description. */
const TYPE_TITLE: Record<string, string> = {
  top_up: "Wallet top-up",
  auto_reload: "Auto-reload",
  top_up_failed: "Top-up failed",
};

/**
 * An admin adjustment is named by its reason code. The description holds the
 * operator's own note on older rows, which is not the customer's to read.
 */
const ADJUSTMENT_TITLE: Record<string, string> = {
  goodwill: "Goodwill adjustment",
  bug_compensation: "Service credit",
  billing_error: "Billing correction",
  sales_promo: "Promotional credit",
  fraud_clawback: "Balance correction",
};

// Stripe object ids (pi_…, re_…, ch_…) mean nothing to a customer.
const STRIPE_ID = /\s*\b(?:for\s+)?(?:pi|pm|ch|re|in|cs|py|sub|seti|cus)_[A-Za-z0-9_]+/g;

const meta = (tx: PaymentTransaction) => tx.metadata ?? {};

function title(tx: PaymentTransaction): string {
  const amount = Math.abs(tx.amount_credits);
  switch (tx.transaction_type) {
    case "adjustment":
      if (meta(tx).reason === "duplicate_stripe_credit") {
        return `Correction: a ${formatMoney(amount)} payment was credited twice`;
      }
      return ADJUSTMENT_TITLE[String(meta(tx).reason_code)] ?? "Account adjustment";
    case "refund":
      return tx.amount_credits < 0 ? "Refund to your card" : "Refund cancelled: credit restored";
    case "subscription_charge":
      return "Pro plan payment";
    case "subscription_refund":
      return tx.amount_credits < 0 ? "Pro plan refund failed" : "Pro plan refund";
  }
  const described = (tx.description ?? "").replace(STRIPE_ID, "").trim();
  return described || TYPE_TITLE[tx.transaction_type];
}

export function paymentRow(tx: PaymentTransaction): PaymentRow {
  const reason = meta(tx).reason;
  if (tx.transaction_type === "top_up_failed") {
    return {
      title: title(tx),
      reason: typeof reason === "string" && reason.trim() ? reason : null,
      amount: formatMoney(Number(meta(tx).attempted_amount ?? 0)),
      note: "Not charged",
      tone: "failed",
    };
  }
  // Paid by card, not from the wallet: the balance did not move, so no balance line.
  if (tx.transaction_type === "subscription_charge") {
    return {
      title: title(tx),
      reason: null,
      amount: formatSignedMoney(-Math.abs(tx.amount_credits)),
      note: "Charged to card",
      tone: "card",
    };
  }
  // Back to the card it was paid with, not the wallet; a negative row takes back one that failed.
  if (tx.transaction_type === "subscription_refund") {
    const refunded = tx.amount_credits >= 0;
    return {
      title: title(tx),
      reason: null,
      amount: formatSignedMoney(tx.amount_credits),
      note: refunded ? "Refunded to card" : "Refund did not go through",
      tone: refunded ? "credit" : "failed",
    };
  }
  return {
    title: title(tx),
    reason: null,
    amount: formatSignedMoney(tx.amount_credits),
    note: `Balance: ${formatMoney(tx.balance_after_credits)}`,
    tone: tx.amount_credits < 0 ? "debit" : "credit",
  };
}
